import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { jobFieldsService } from '../../../services/supabaseService';

const JobFieldsConfigSection = () => {
  const defaultJobFields = [
    {
      id: 1,
      fieldName: 'Job Title',
      fieldKey: 'jobTitle',
      fieldType: 'text',
      required: false,
      enabled: true,
      placeholder: 'e.g., Senior Software Engineer',
      description: 'Employee job title or position name'
    },
    {
      id: 2,
      fieldName: 'Employment Type',
      fieldKey: 'employmentType',
      fieldType: 'select',
      required: false,
      enabled: true,
      options: ['Full-time', 'Part-time', 'Contract', 'Intern', 'Temporary'],
      description: 'Type of employment contract'
    },
    {
      id: 3,
      fieldName: 'Manager/Supervisor',
      fieldKey: 'manager',
      fieldType: 'text',
      required: false,
      enabled: true,
      placeholder: 'Enter manager name',
      description: 'Direct supervisor or manager'
    },
    {
      id: 4,
      fieldName: 'Work Location',
      fieldKey: 'workLocation',
      fieldType: 'text',
      required: false,
      enabled: true,
      placeholder: 'e.g., New York Office, Remote',
      description: 'Primary work location'
    },
    {
      id: 5,
      fieldName: 'Annual Salary',
      fieldKey: 'salary',
      fieldType: 'number',
      required: false,
      enabled: true,
      placeholder: 'e.g., 75000',
      description: 'Annual salary amount'
    },
    {
      id: 6,
      fieldName: 'Hire Date',
      fieldKey: 'hireDate',
      fieldType: 'date',
      required: true,
      enabled: true,
      description: 'Employee start date'
    },
    {
      id: 7,
      fieldName: 'Employment Status',
      fieldKey: 'status',
      fieldType: 'select',
      required: true,
      enabled: true,
      options: ['Active', 'On Leave', 'Suspended', 'Terminated'],
      description: 'Current employment status'
    }
  ];

  const [jobFields, setJobFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [formData, setFormData] = useState({
    fieldName: '',
    fieldKey: '',
    fieldType: 'text',
    required: false,
    enabled: true,
    placeholder: '',
    description: '',
    options: []
  });

  // Load job fields from Supabase on component mount
  useEffect(() => {
    loadJobFields();
  }, []);

  const loadJobFields = async () => {
    try {
      setLoading(true);
      const data = await jobFieldsService?.getAll();
      setJobFields(data);
    } catch (error) {
      console.error('Error loading job fields:', error);
    } finally {
      setLoading(false);
    }
  };

  const fieldTypeOptions = [
    { value: 'text', label: 'Text Input' },
    { value: 'number', label: 'Number Input' },
    { value: 'date', label: 'Date Picker' },
    { value: 'select', label: 'Dropdown Select' },
    { value: 'textarea', label: 'Text Area' },
    { value: 'email', label: 'Email Input' },
    { value: 'phone', label: 'Phone Input' }
  ];

  const handleAdd = () => {
    setIsAdding(true);
    setEditingId(null);
    setFormData({
      fieldName: '',
      fieldKey: '',
      fieldType: 'text',
      required: false,
      enabled: true,
      placeholder: '',
      description: '',
      options: []
    });
  };

  const handleEdit = (field) => {
    setEditingId(field?.id);
    setIsAdding(false);
    setFormData({
      fieldName: field?.fieldName,
      fieldKey: field?.fieldKey,
      fieldType: field?.fieldType,
      required: field?.required,
      enabled: field?.enabled,
      placeholder: field?.placeholder || '',
      description: field?.description,
      options: field?.options || []
    });
  };

  const handleSave = async () => {
    try {
      if (isAdding) {
        const newField = await jobFieldsService?.create(formData);
        if (newField) {
          setJobFields(prev => [...prev, newField]);
          setIsAdding(false);
        }
      } else {
        const updatedField = await jobFieldsService?.update(editingId, formData);
        if (updatedField) {
          setJobFields(prev => prev?.map(f => f?.id === editingId ? updatedField : f));
          setEditingId(null);
        }
      }
      setFormData({
        fieldName: '',
        fieldKey: '',
        fieldType: 'text',
        required: false,
        enabled: true,
        placeholder: '',
        description: '',
        options: []
      });
    } catch (error) {
      console.error('Error saving job field:', error);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsAdding(false);
    setFormData({
      fieldName: '',
      fieldKey: '',
      fieldType: 'text',
      required: false,
      enabled: true,
      placeholder: '',
      description: '',
      options: []
    });
  };

  const handleDelete = (id) => {
    setDeletingId(id);
  };

  const confirmDelete = async () => {
    try {
      const success = await jobFieldsService?.delete(deletingId);
      if (success) {
        setJobFields(prev => prev?.filter(f => f?.id !== deletingId));
      }
      setDeletingId(null);
    } catch (error) {
      console.error('Error deleting job field:', error);
      setDeletingId(null);
    }
  };

  const cancelDelete = () => {
    setDeletingId(null);
  };

  const toggleFieldEnabled = async (id) => {
    try {
      const field = jobFields?.find(f => f?.id === id);
      if (field) {
        const updatedField = await jobFieldsService?.update(id, { enabled: !field?.enabled });
        if (updatedField) {
          setJobFields(prev => prev?.map(f => f?.id === id ? updatedField : f));
        }
      }
    } catch (error) {
      console.error('Error toggling field enabled:', error);
    }
  };

  const generateFieldKey = (name) => {
    return name?.toLowerCase()?.replace(/[^a-z0-9]+/g, '')?.replace(/^[0-9]/, '_$&');
  };

  const handleFieldNameChange = (value) => {
    setFormData(prev => ({
      ...prev,
      fieldName: value,
      fieldKey: generateFieldKey(value)
    }));
  };

  const renderFieldForm = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Field Name"
          value={formData?.fieldName}
          onChange={(e) => handleFieldNameChange(e?.target?.value)}
          placeholder="e.g., Department Code"
        />
        <Input
          label="Field Key (Auto-generated)"
          value={formData?.fieldKey}
          onChange={(e) => setFormData({ ...formData, fieldKey: e?.target?.value })}
          placeholder="e.g., departmentCode"
          disabled
        />
      </div>

      <Select
        label="Field Type"
        options={fieldTypeOptions}
        value={formData?.fieldType}
        onChange={(value) => setFormData({ ...formData, fieldType: value })}
      />

      <div>
        <label className="block text-sm font-medium text-foreground mb-1.5">
          Description
        </label>
        <textarea
          value={formData?.description}
          onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
          placeholder="Brief description of this field"
          rows={2}
          className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
        />
      </div>

      {(formData?.fieldType === 'text' || formData?.fieldType === 'number' || formData?.fieldType === 'email' || formData?.fieldType === 'phone') && (
        <Input
          label="Placeholder Text"
          value={formData?.placeholder}
          onChange={(e) => setFormData({ ...formData, placeholder: e?.target?.value })}
          placeholder="Placeholder text for the input field"
        />
      )}

      {formData?.fieldType === 'select' && (
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            Dropdown Options (comma-separated)
          </label>
          <Input
            value={formData?.options?.join(', ')}
            onChange={(e) => setFormData({ 
              ...formData, 
              options: e?.target?.value?.split(',')?.map(opt => opt?.trim())?.filter(Boolean)
            })}
            placeholder="Option 1, Option 2, Option 3"
          />
        </div>
      )}

      <div className="flex gap-4">
        <Checkbox
          label="Required Field"
          description="Users must fill this field"
          checked={formData?.required}
          onChange={(e) => setFormData({ ...formData, required: e?.target?.checked })}
        />
        <Checkbox
          label="Enabled"
          description="Show this field in forms"
          checked={formData?.enabled}
          onChange={(e) => setFormData({ ...formData, enabled: e?.target?.checked })}
        />
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t border-border">
        <Button variant="outline" size="sm" onClick={handleCancel}>
          Cancel
        </Button>
        <Button 
          variant="default" 
          size="sm" 
          onClick={handleSave}
          disabled={!formData?.fieldName?.trim() || !formData?.fieldKey?.trim()}
        >
          {isAdding ? 'Add Field' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="p-6">
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-sm text-muted-foreground">Loading job fields...</p>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-6 flex items-start justify-between">
            <div>
              <h2 className="font-heading font-semibold text-xl text-foreground mb-1">
                Job Detail Fields Configuration
              </h2>
              <p className="font-body text-sm text-muted-foreground">
                Customize job-related fields that appear in employee forms and profiles
              </p>
            </div>
            <Button
              variant="default"
              size="sm"
              iconName="Plus"
              onClick={handleAdd}
              disabled={isAdding || editingId !== null}
            >
              Add Field
            </Button>
          </div>

          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-primary mt-0.5" />
              <div>
                <h4 className="font-heading font-semibold text-sm text-foreground mb-1">
                  Custom Field Configuration
                </h4>
                <p className="text-sm text-muted-foreground">
                  Add, edit, or remove job detail fields to match your organization's needs. Disabled fields will be hidden from employee forms but data will be preserved.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {isAdding && (
              <div className="bg-card border-2 border-primary rounded-lg p-6">
                <div className="mb-4">
                  <h3 className="font-heading font-semibold text-lg text-foreground mb-2">
                    Add New Field
                  </h3>
                </div>
                {renderFieldForm()}
              </div>
            )}

            {jobFields?.map((field) => (
              <div
                key={field?.id}
                className={`bg-card border rounded-lg p-4 ${
                  field?.enabled ? 'border-border' : 'border-muted-foreground/30 opacity-60'
                }`}
              >
                {editingId === field?.id ? (
                  <div>
                    <div className="mb-4">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-2">
                        Edit Field
                      </h3>
                    </div>
                    {renderFieldForm()}
                  </div>
                ) : (
                  <div>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                          <Icon 
                            name={field?.fieldType === 'select' ? 'List' : field?.fieldType === 'date' ? 'Calendar' : field?.fieldType === 'number' ? 'Hash' : 'Type'} 
                            size={20} 
                            className="text-primary" 
                          />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-heading font-semibold text-base text-foreground">
                              {field?.fieldName}
                            </h4>
                            {field?.required && (
                              <span className="text-xs bg-destructive/10 text-destructive px-2 py-0.5 rounded">
                                Required
                              </span>
                            )}
                            {!field?.enabled && (
                              <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded">
                                Disabled
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mt-0.5">
                            {field?.description}
                          </p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs text-muted-foreground">
                              Key: <span className="font-mono text-foreground">{field?.fieldKey}</span>
                            </span>
                            <span className="text-xs text-muted-foreground">
                              •
                            </span>
                            <span className="text-xs text-primary font-semibold">
                              {fieldTypeOptions?.find(opt => opt?.value === field?.fieldType)?.label}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName={field?.enabled ? "EyeOff" : "Eye"}
                          onClick={() => toggleFieldEnabled(field?.id)}
                          disabled={isAdding || editingId !== null}
                          title={field?.enabled ? "Disable field" : "Enable field"}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Edit2"
                          onClick={() => handleEdit(field)}
                          disabled={isAdding || editingId !== null}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Trash2"
                          onClick={() => handleDelete(field?.id)}
                          disabled={isAdding || editingId !== null}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        />
                      </div>
                    </div>

                    {field?.fieldType === 'select' && field?.options?.length > 0 && (
                      <div className="bg-muted/30 rounded-lg p-3 mt-3">
                        <div className="flex items-center gap-2 mb-2">
                          <Icon name="List" size={14} className="text-muted-foreground" />
                          <span className="text-xs font-semibold text-muted-foreground">Dropdown Options</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {field?.options?.map((option, idx) => (
                            <span key={idx} className="text-xs bg-background px-2 py-1 rounded border border-border text-foreground">
                              {option}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {field?.placeholder && (
                      <div className="bg-muted/30 rounded-lg p-3 mt-3">
                        <div className="flex items-center gap-2">
                          <Icon name="MessageSquare" size={14} className="text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">
                            Placeholder: <span className="text-foreground italic">"{field?.placeholder}"</span>
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Delete Confirmation Dialog */}
          {deletingId && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-card rounded-lg p-6 max-w-md w-full mx-4 elevation-lg">
                <div className="flex items-start gap-3 mb-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-destructive/10">
                    <Icon name="AlertTriangle" size={24} className="text-destructive" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-semibold text-lg text-foreground mb-1">
                      Delete Field
                    </h3>
                    <p className="text-sm text-muted-foreground mb-2">
                      Are you sure you want to delete <span className="font-semibold text-foreground">{jobFields?.find(f => f?.id === deletingId)?.fieldName}</span>?
                    </p>
                    <p className="text-xs text-warning">
                      Warning: This will remove the field from all employee forms. Existing data will be preserved but not visible.
                    </p>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={cancelDelete}>
                    Cancel
                  </Button>
                  <Button variant="destructive" size="sm" onClick={confirmDelete}>
                    Delete Field
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default JobFieldsConfigSection;